'use client';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Facebook, Twitter, Youtube, Instagram, Check } from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';
import { NewsletterAPI } from '@/lib/api';
import { useState } from 'react';
import { toast } from 'sonner';

export default function Footer() {

  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");

  const handleSubscribe = async () => {
    if (!email.trim()) {
      toast.error("Please enter your email address.");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!emailRegex.test(email)) {
      toast.error("Please enter a valid email address.");
      return;
    }

    try {
      setLoading(true);

      const res = await NewsletterAPI.subscribe(email);

      if (res.meta.status) {
        toast.success("🎉 Successfully subscribed to our newsletter!", {
          description: "You'll now receive our latest updates and exclusive offers.",
          duration: 4000,
        });

        setEmail("");
      } else {
        toast.error(res.meta.message || "Subscription failed.");
      }
    } catch (error) {
      console.error(error);

      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className="bg-secondary text-white">
      {/* Newsletter Section */}
      <section className="py-16 border-b border-gray-800">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Column - Newsletter */}
            <div className="space-y-6">
              <h3 className="text-2xl font-bold">
                Sign Up for Exclusive Offers From Us
              </h3>
              <p className="text-gray-300">
                Stay updated with our latest deals, special promotions, and insider access to luxury villa experiences.
              </p>

              {/* Newsletter Form */}
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* <Input
                    placeholder="Your Name"
                    className="bg-white placeholder-gray-800 text-black focus:border-yellow-400"
                  /> */}
                  <Input
                    type="email"
                    placeholder="Your Email Address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="bg-white placeholder-gray-800 text-black focus:border-yellow-400"
                  />
                  <Button
                    onClick={handleSubscribe}
                    disabled={loading}
                  >
                    <Check className="w-4 h-4 mr-2" />
                    {loading ? "Signing Up..." : "Sign Up"}
                  </Button>

                </div>

              </div>

              <p className="text-sm text-gray-400">
                By signing up, you agree to receive marketing communications from us. You can unsubscribe at any time.
              </p>

              {/* Social Media Icons */}
              <div className="flex gap-4">
                <div className="w-10 h-10 bg-gray-800 border border-gray-600 rounded flex items-center justify-center hover:bg-gray-700 transition-colors cursor-pointer">
                  <Link href=" https://www.facebook.com/share/1JF6ubvAs2/?mibextid=wwXIfr" target="_blank">
                    <Facebook className="w-5 h-5" />
                  </Link>
                </div>
                {/* <div className="w-10 h-10 bg-gray-800 border border-gray-600 rounded flex items-center justify-center hover:bg-gray-700 transition-colors cursor-pointer">
                    <Link href="https://twitter.com/18bricks" target="_blank">
                      <Twitter className="w-5 h-5" />
                    </Link>
                  </div>
                  <div className="w-10 h-10 bg-gray-800 border border-gray-600 rounded flex items-center justify-center hover:bg-gray-700 transition-colors cursor-pointer">
                    <Youtube className="w-5 h-5" />
                  </div> */}

                <div className="w-10 h-10 bg-gray-800 border border-gray-600 rounded flex items-center justify-center hover:bg-gray-700 transition-colors cursor-pointer">
                  <Link href="https://www.instagram.com/18bricksholidayhomes?igsh=MXVwMGh5NXppcHJnZw==/" target="_blank">
                    <Instagram className="w-5 h-5" />
                  </Link>
                </div>
              </div>
            </div>

            {/* Right Column - Quick Links */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              {/* Company Links */}
              <div className="space-y-4">
                <h4 className="font-bold text-lg relative">
                  Company
                  <div className="absolute bottom-0 left-0 w-8 h-0.5 bg-yellow-400"></div>
                </h4>
                <ul className="space-y-2">
                  <li><Link href="/about" className="text-gray-300 hover:text-white transition-colors">About Us</Link></li>
                  {/* <li><Link href="/leadership" className="text-gray-300 hover:text-white transition-colors">Leadership</Link></li> */}
                  {/* <li><Link href="/career" className="text-gray-300 hover:text-white transition-colors">Careers</Link></li> */}
                  <li><Link href="/contact" className="text-gray-300 hover:text-white transition-colors">Contact Us</Link></li>
                </ul>
              </div>

              {/* Services Links */}
              <div className="space-y-4">
                <h4 className="font-bold text-lg relative">
                  Services
                  <div className="absolute bottom-0 left-0 w-8 h-0.5 bg-yellow-400"></div>
                </h4>
                <ul className="space-y-2">
                  {/* <li><a href="#" className="text-gray-300 hover:text-white transition-colors">Family Experiences</a></li> */}
                  <li><a href="/services" className="text-gray-300 hover:text-white transition-colors">Services</a></li>
                  <li><a href="/corporate/" className="text-gray-300 hover:text-white transition-colors">Corporate & Groups</a></li>
                  {/* <li><a href="#" className="text-gray-300 hover:text-white transition-colors">Tours</a></li> */}
                </ul>
              </div>

              {/* Hosting Links */}


              {/* Support Links */}
              <div className="space-y-4">
                <h4 className="font-bold text-lg relative">
                  Support
                  <div className="absolute bottom-0 left-0 w-8 h-0.5 bg-yellow-400"></div>
                </h4>
                <ul className="space-y-2">
                  {/* <li><a href="#" className="text-gray-300 hover:text-white transition-colors">FAQ</a></li> */}
                  <li><Link href="/privacy" className="text-gray-300 hover:text-white transition-colors">Privacy Policy</Link></li>
                  <li><Link href="/terms" className="text-gray-300 hover:text-white transition-colors">Terms & Conditions</Link></li>
                  {/* <li><a href="#" className="text-gray-300 hover:text-white transition-colors">Sitemap</a></li> */}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Bottom Strip */}
      <section className="py-8">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            {/* Logo and Brand */}
            <div className="flex items-center gap-4">
              <Image
                src="/images/footerlogo.png"
                alt="18Bricks"
                width={60}
                height={60}
              // className='w-20 h-20'
              />
              <div>
                <h5 className="font-bold text-lg">18 BRICKS HOLIDAY HOMES RENTAL L.L.C</h5>
                <p className="text-sm text-gray-400 max-w-xs">
                  Your trusted partner for luxury property experiences in Dubai and beyond.
                </p>
              </div>
            </div>

            {/* Copyright */}
            <div className="text-sm text-gray-400 text-center md:text-right">
              <p>Copyright © 2026 18 BRICKS HOLIDAY HOMES RENTAL L.L.C</p>
              <p>All rights reserved.</p>
            </div>
          </div>
        </div>
      </section>
    </footer>
  );
}
